import { useState } from "react";
import { Link } from "wouter";
import { PILLAR_DETAIL, type Pillars } from "@/components/powlo/home/exampleScore";
import { SCORE_BANDS, bandFor, type PillarDisplay } from "@/utils/planViewModel";

const PILLAR_DETAIL_KEY: Record<PillarDisplay["apiKey"], keyof Pillars> = {
  generation_potential: "generation",
  economic_opportunity: "economic",
  energy_independence: "independence",
  property_performance: "property",
  smart_energy_readiness: "smartReady",
  optimisation_potential: "systemFit",
};

export default function ScoreBreakdown({
  pillars,
  overall,
}: {
  pillars: PillarDisplay[];
  overall: number;
}) {
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const band = bandFor(overall);

  if (pillars.length === 0) return null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-end">
        <Link href="/methodology" className="text-sm font-semibold text-powlo-lime">
          How we score →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {pillars.map((pillar) => {
          const score = Math.round(pillar.score);
          const color = bandFor(score).color;
          const isOpen = openInfo === pillar.apiKey;
          return (
            <div key={pillar.apiKey} className="flex flex-col gap-2 rounded-2xl bg-powlo-navy-light p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold text-[#C9D1DE]">
                  {pillar.label}
                  <span className="mt-0.5 block text-[11px] font-normal text-[#8FA0B8]">{pillar.nickname}</span>
                </span>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`What is ${pillar.label}?`}
                  onClick={() => setOpenInfo(isOpen ? null : pillar.apiKey)}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-powlo-navy text-[10px] font-bold text-[#8FA0B8]"
                >
                  i
                </button>
              </div>
              <div className="font-powlo-display text-2xl font-bold text-white">
                {score}
                <span className="text-sm font-normal text-[#8FA0B8]"> /100</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-powlo-navy">
                <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(0, score))}%`, background: color }} />
              </div>
              {pillar.fixed && (
                <span className="text-[10px] text-[#8FA0B8]">Fixed — a property fact.</span>
              )}
              {isOpen && (
                <div className="flex flex-col gap-1">
                  <p className="m-0 text-xs leading-relaxed text-[#C9D1DE]">
                    {PILLAR_DETAIL[PILLAR_DETAIL_KEY[pillar.apiKey]]}
                  </p>
                  <p className="m-0 text-xs text-[#8FA0B8]">
                    {Math.round(pillar.weight * 100)}% of the Home Power Score.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-xs font-bold tracking-widest text-[#8FA0B8]">WHAT THE SCORE MEANS</div>
        <div className="relative flex h-3 w-full overflow-visible rounded-full">
          {SCORE_BANDS.map((item, index) => (
            <div
              key={item.label}
              className={index === 0 ? "rounded-l-full" : index === SCORE_BANDS.length - 1 ? "rounded-r-full" : ""}
              style={{ width: `${item.width}%`, background: item.color }}
            />
          ))}
          <span
            aria-hidden="true"
            className="absolute top-1/2 h-5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
            style={{ left: `${Math.min(100, Math.max(0, overall))}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] font-semibold text-[#8FA0B8]">
          {SCORE_BANDS.map((item) => (
            <span key={item.label}>{item.label}</span>
          ))}
        </div>
        <p className="m-0 text-xs leading-relaxed text-[#C9D1DE]">
          Your score is <strong className="text-white">{Math.round(overall)}</strong> —{" "}
          <strong className="text-white">{band.label}</strong>.
        </p>
      </div>
    </div>
  );
}

export function MiniRing({ score, color }: { score: number; color: string }) {
  const circumference = 2 * Math.PI * 29;
  const dash = (Math.min(100, Math.max(0, score)) / 100) * circumference;
  return (
    <span className="relative block h-[62px] w-[62px] shrink-0">
      <svg width="62" height="62" viewBox="0 0 68 68" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="34" cy="34" r="29" fill="none" stroke="#16304F" strokeWidth="7" />
        <circle cx="34" cy="34" r="29" fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-powlo-display text-lg font-bold text-white">{Math.round(score)}</span>
    </span>
  );
}

export function BigRing({ score, color }: { score: number; color: string }) {
  const circumference = 2 * Math.PI * 64;
  const dash = (Math.min(100, Math.max(0, score)) / 100) * circumference;
  return (
    <span className="relative block h-[180px] w-[180px]">
      <svg width="180" height="180" viewBox="0 0 160 160" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="80" cy="80" r="64" fill="none" stroke="#16304F" strokeWidth="14" />
        <circle cx="80" cy="80" r="64" fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-powlo-display text-5xl font-bold tracking-tight text-white">{Math.round(score)}</span>
        <span className="text-sm text-[#8FA0B8]">/100</span>
      </span>
    </span>
  );
}
